import { type FunctionComponent } from "react";
import { CheckCircleOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, Popover, Space, Tag, Typography } from "antd";

import { type ScheduledAssignHabitsAutomation } from "@fitness-app/data-models/entities/AutomationTemplate";

import { formatSentTime } from "~/modules/Automation/utils/formatSentTime";
import { supabase } from "~/store/initializeStore";

interface OwnProps {
  item: ScheduledAssignHabitsAutomation;
  onEdit: () => void;
  onRemove: () => void;
}

type Props = OwnProps;

const AssignHabitsItem: FunctionComponent<Props> = ({ item, onEdit, onRemove }) => {
  const sentTimeLabel = formatSentTime(item.sentTime);

  const habitTemplatesQuery = useQuery({
    queryKey: ["automation-habit-templates-names", item.habitTemplateIds],
    staleTime: 60_000,
    enabled: item.habitTemplateIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("habit_template")
        .select("id, name")
        .in("id", item.habitTemplateIds)
        .returns<{ id: string; name: string }[]>();

      if (error) {
        throw error;
      }

      return data ?? [];
    },
  });

  const habitNames =
    habitTemplatesQuery.data?.map((template) => template.name) ?? item.habitTemplateIds;

  return (
    <Popover
      title="Przypisanie nawyków"
      content={
        <Space direction="vertical" className="min-w-[220px] max-w-[320px]">
          <Space>
            <Typography.Text type="secondary">Nazwa:</Typography.Text>
            <Typography.Text>{item.name}</Typography.Text>
          </Space>
          <Space align="start">
            <Typography.Text type="secondary">Nawyki:</Typography.Text>
            <Space direction="vertical" size={0}>
              {habitNames.map((name) => (
                <Typography.Text key={name}>{name}</Typography.Text>
              ))}
            </Space>
          </Space>
          <Space>
            <Typography.Text type="secondary">Czas realizacji:</Typography.Text>
            <Typography.Text>{sentTimeLabel}</Typography.Text>
          </Space>
          <Space>
            <Button icon={<EditOutlined />} type="text" onClick={onEdit} />
            <Button danger icon={<DeleteOutlined />} type="text" onClick={onRemove} />
          </Space>
        </Space>
      }
    >
      <span className="cursor-pointer">
        <CheckCircleOutlined className="mr-2" />
        <span>{sentTimeLabel}</span>
        <Tag color="green" className="ml-2">
          Nawyki
        </Tag>
        <div className="max-w-[140px] truncate text-xs text-gray-500">{item.name}</div>
      </span>
    </Popover>
  );
};

export default AssignHabitsItem;
